import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateChild } from '@angular/router';
import { AuthService } from '@core/service/auth.service';
import { PackageLimitService } from '@shared/service/package-limit.service';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  constructor(private router: Router, private authService: AuthService, private packageLimitService: PackageLimitService) { }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.validateAccess(state.url);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.validateAccess(state.url);
  }

  private validateAccess(redirectUrl: string): Observable<boolean | UrlTree> | boolean | UrlTree {
    if (!this.authService.isLogin()) {
      return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: redirectUrl } });
    }

    // Check trial expiration for logged-in users
    const claimRaw = localStorage.getItem('userClaim');
    if (!claimRaw) {
      return true; // No claim, allow access (will be handled by login check)
    }

    try {
      const parsed = JSON.parse(claimRaw);
      const schoolId = Number(parsed.SchoolId) || Number(parsed.schoolId) || null;
      
      if (!schoolId) {
        return true; // No schoolId, allow access
      }

      // Check if trial is expired
      return this.packageLimitService.getSchoolConfiguration(schoolId).pipe(
        map((schoolConfig) => {
          if (schoolConfig) {
            const limits = this.packageLimitService.getPackageLimits(schoolConfig);
            const packageName = limits.package.toLowerCase();
            
            if (limits.isTrialExpired && packageName === 'trial') {
              // Trial expired - logout and redirect to login with message
              this.authService.logout();
              return this.router.createUrlTree(['/login'], {
                queryParams: {
                  expired: 'true',
                  message: 'Your trial period has expired. Please upgrade your plan to continue.'
                }
              });
            }
          }
          return true; // Subscription not expired or not trial/basic package
        }),
        catchError((error) => {
          console.error('Error checking trial expiration in guard:', error);
          // If we can't check, allow access (fail open)
          return of(true);
        })
      );
    } catch {
      return true; // Error parsing claim, allow access
    }
  }
}
